/**
 * PortalGrantsPanel — who can see which patient in the clinician portal
 * (clinician-portal P1-D14, §2.8 F-7).
 *
 * P0 shipped the ability to REVOKE a grant and no way to see what there was to
 * revoke, which made the primitive unreachable in practice. This is that list.
 *
 * MOST GRANTS ARE AUTOMATIC. At consent, both the referring provider and whoever
 * typed the referral in receive one (D-18) — a front-desk staffer who submitted
 * on a physician's behalf still has to be able to see what happened. The manual
 * path here is for the case with no referral behind it: a practice that took over
 * care, or a phoned-in request.
 *
 * REVOKING IS NOT DELETING. The row stays with `revoked_at` set, because the
 * grant is the audit record of who could see whom and for how long.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, KeyRound, Loader2, Plus, RefreshCw, ShieldOff } from 'lucide-react';
import api from '../../../services/api';
import Modal from '../../components/forms/Modal';
import { FilterBar, SearchInput } from '../../components/filters';
import { dangerBtnCls, fieldCls, primaryBtnCls, secondaryBtnCls } from '../../components/filters/styles';
import { Alert } from '../../components/ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface Grant {
  id: string;
  contact_id: string;
  portal_user_id: string;
  referral_id: string | null;
  granted_via: 'referral' | 'manual';
  study_id: string | null;
  created_at: string;
  revoked_at: string | null;
  clinician_name: string | null;
  clinician_email: string | null;
  clinician_status: string | null;
  contact_name: string | null;
  study_label: string | null;
  live: boolean;
}

interface PortalUserOption { id: string; name: string; email: string; status: string }

/**
 * One (clinician, patient) pair — the unit access is really granted at. The
 * flat rows underneath are the audit trail of HOW that access accumulated
 * (referral consents, manual grants, study scopes), so the list groups them.
 */
interface GrantGroup {
  key: string;
  portal_user_id: string;
  contact_id: string;
  clinician_name: string | null;
  clinician_email: string | null;
  contact_name: string | null;
  rows: Grant[];
  liveCount: number;
}

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';

// ---------------------------------------------------------------------------

function ManualGrantModal({ clinicians, onClose, onGranted }: {
  clinicians: PortalUserOption[];
  onClose: () => void;
  onGranted: (message: string) => void;
}) {
  const [portalUserId, setPortalUserId] = useState(clinicians[0]?.id || '');
  const [contactId, setContactId] = useState('');
  const [studyId, setStudyId] = useState('');
  const [studies, setStudies] = useState<{ id: string; label: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSend = portalUserId && contactId.trim() && !busy;

  // Live (unarchived) crm.studies rows for the scope select. Non-fatal on
  // failure — the select just offers referral-steps-only.
  useEffect(() => {
    let alive = true;
    api.get('/api/crm/studies')
      .then((res) => {
        if (!alive) return;
        const list = (res.data?.studies || res.data || [])
          .filter((s: any) => s.id && !s.archived_at)
          .map((s: any) => ({ id: s.id as string, label: (s.title || s.slug || s.id) as string }));
        setStudies(list);
      })
      .catch(() => { /* select stays referral-steps-only */ });
    return () => { alive = false; };
  }, []);

  const grant = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/crm/portal-admin/grants', {
        portal_user_id: portalUserId,
        contact_id: contactId.trim(),
        ...(studyId ? { study_id: studyId } : {}),
      });
      onGranted(
        res.data?.outcome === 'already'
          ? 'That clinician already had access to this patient.'
          : 'Access granted.',
      );
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Grant access to a patient">
      <div className="space-y-3">
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          Grants are created automatically when a referred patient consents. Use this only when there is no
          referral behind it — a practice taking over care, or a request that came in by phone.
        </p>
        <div>
          <label htmlFor="grant-clinician" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Clinician
          </label>
          <select id="grant-clinician" className={fieldCls} value={portalUserId} onChange={(e) => setPortalUserId(e.target.value)}>
            {clinicians.length === 0 && <option value="">No active clinicians</option>}
            {clinicians.map((c) => (
              <option key={c.id} value={c.id}>{c.name} — {c.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="grant-contact" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Patient (contact ID)
          </label>
          <input
            id="grant-contact"
            className={fieldCls}
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            aria-describedby="grant-contact-help"
          />
          <p id="grant-contact-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Copy it from the patient's CRM profile URL. An ID rather than a name search, on purpose — a
            name-matching picker here would be a way to browse patients from a permissions screen.
          </p>
        </div>
        <div>
          <label htmlFor="grant-study" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Study scope
          </label>
          <select
            id="grant-study"
            className={fieldCls}
            value={studyId}
            onChange={(e) => setStudyId(e.target.value)}
            aria-describedby="grant-study-help"
          >
            <option value="">No study scope — referral steps only</option>
            {studies.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <p id="grant-study-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Without a study, the clinician sees the referral phase only — no enrollment ladder (P2-D3). Pick the
            study to light the ladder.
          </p>
        </div>
        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className={primaryBtnCls} onClick={grant} disabled={!canSend}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Grant access
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

export default function PortalGrantsPanel({ clinicians }: { clinicians: PortalUserOption[] }) {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [search, setSearch] = useState('');
  const [showGrant, setShowGrant] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [revokeAllGroup, setRevokeAllGroup] = useState<GrantGroup | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/portal-admin/grants', {
        params: includeRevoked ? { include_revoked: 'true' } : {},
      });
      setGrants(res.data?.grants || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [includeRevoked]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await api.post(`/api/crm/portal-admin/grants/${id}/revoke`);
      setNotice('Access revoked. The clinician loses it on their very next request, including the signed PDF.');
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const revokeAll = async (g: GrantGroup) => {
    setBusyId(g.key);
    setError(null);
    try {
      const res = await api.post('/api/crm/portal-admin/grants/revoke-all', {
        portal_user_id: g.portal_user_id,
        contact_id: g.contact_id,
      });
      const n = res.data?.grants_revoked ?? 0;
      setNotice(`${n} grant${n === 1 ? '' : 's'} revoked. The clinician loses access on their very next request.`);
      setRevokeAllGroup(null);
      await load();
    } catch (e) {
      setError(errMsg(e));
      setRevokeAllGroup(null);
    } finally {
      setBusyId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const shown = q
    ? grants.filter((g) => [g.clinician_name, g.clinician_email, g.contact_name]
        .filter(Boolean).join(' ').toLowerCase().includes(q))
    : grants;

  // Group by (clinician, patient) in first-appearance order — the server list
  // is newest-first, so the most recently touched pair stays on top.
  const groups = useMemo(() => {
    const byKey = new Map<string, GrantGroup>();
    for (const g of shown) {
      const key = `${g.portal_user_id}::${g.contact_id}`;
      let group = byKey.get(key);
      if (!group) {
        group = {
          key,
          portal_user_id: g.portal_user_id,
          contact_id: g.contact_id,
          clinician_name: null,
          clinician_email: null,
          contact_name: null,
          rows: [],
          liveCount: 0,
        };
        byKey.set(key, group);
      }
      group.clinician_name = group.clinician_name || g.clinician_name;
      group.clinician_email = group.clinician_email || g.clinician_email;
      group.contact_name = group.contact_name || g.contact_name;
      group.rows.push(g);
      if (g.live) group.liveCount += 1;
    }
    return [...byKey.values()];
  }, [shown]);

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-3">
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search clinician or patient" />
        <label className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 sm:min-h-0">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={includeRevoked}
            onChange={(e) => setIncludeRevoked(e.target.checked)}
          />
          Show revoked
        </label>
        <button type="button" className={secondaryBtnCls} onClick={load} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button type="button" className={primaryBtnCls} onClick={() => setShowGrant(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Grant access
        </button>
      </FilterBar>

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}
      {notice && (
        <Alert tone="success" icon={false}>{notice}</Alert>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Clinician</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Patient</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">How</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Since</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
                </td></tr>
              )}
              {!loading && shown.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  <KeyRound className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
                  No clinician can see any patient yet. Grants appear here automatically when a referred
                  patient consents.
                </td></tr>
              )}
              {!loading && groups.map((group) => {
                const open = openGroups.has(group.key);
                return (
                  <Fragment key={group.key}>
                    <tr className="bg-gray-50/70 transition-colors duration-150 hover:bg-gray-50 dark:bg-gray-900/30 dark:hover:bg-gray-700/50">
                      <td colSpan={4} className="px-4 py-2.5">
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.key)}
                          aria-expanded={open}
                          className="flex w-full min-w-0 items-center gap-2 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          {open
                            ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                          <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                            {group.clinician_name || group.clinician_email || '—'}
                          </span>
                          <span className="text-gray-400">—</span>
                          <span className="truncate text-sm text-gray-900 dark:text-gray-100">
                            {group.contact_name || '—'}
                          </span>
                          <span className="flex-shrink-0 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                            {group.rows.length} grant{group.rows.length === 1 ? '' : 's'} · {group.liveCount} live
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {group.liveCount > 0 && (
                          <button
                            type="button"
                            className={secondaryBtnCls}
                            disabled={busyId === group.key}
                            onClick={() => setRevokeAllGroup(group)}
                          >
                            {busyId === group.key
                              ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                              : <ShieldOff className="mr-1.5 h-4 w-4" />}
                            Revoke all
                          </button>
                        )}
                      </td>
                    </tr>
                    {open && group.rows.map((g) => (
                      <tr key={g.id} className={`transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${g.live ? '' : 'opacity-60'}`}>
                        <td className="px-4 py-3 pl-10 text-sm">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{g.clinician_name || '—'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{g.clinician_email}</div>
                          {g.clinician_status && g.clinician_status !== 'active' && (
                            <span className="mt-0.5 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {g.clinician_status}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {g.contact_name || <span className="text-gray-400">—</span>}
                          {g.study_label && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">{g.study_label}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {g.granted_via === 'referral' ? 'Referral consent' : 'Granted by staff'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {fmt(g.created_at)}
                          {!g.live && (
                            <div className="text-xs text-gray-400">revoked {fmt(g.revoked_at)}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {g.live ? (
                            <button
                              type="button"
                              className={secondaryBtnCls}
                              onClick={() => revoke(g.id)}
                              disabled={busyId === g.id}
                            >
                              {busyId === g.id
                                ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                                : <ShieldOff className="mr-1.5 h-4 w-4" />}
                              Revoke
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">revoked</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showGrant && (
        <ManualGrantModal
          clinicians={clinicians}
          onClose={() => setShowGrant(false)}
          onGranted={(message) => { setNotice(message); load(); }}
        />
      )}

      {revokeAllGroup && (
        <Modal isOpen onClose={() => setRevokeAllGroup(null)} title="Revoke all access" maxWidth="max-w-md">
          <div className="space-y-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Revokes {revokeAllGroup.liveCount} live grant{revokeAllGroup.liveCount === 1 ? '' : 's'}; takes effect on
              the clinician's next request.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {revokeAllGroup.clinician_name || revokeAllGroup.clinician_email || 'This clinician'} loses all access
              to {revokeAllGroup.contact_name || 'this patient'}.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className={secondaryBtnCls}
                onClick={() => setRevokeAllGroup(null)}
                disabled={busyId === revokeAllGroup.key}
              >
                Cancel
              </button>
              <button
                type="button"
                className={dangerBtnCls}
                onClick={() => revokeAll(revokeAllGroup)}
                disabled={busyId === revokeAllGroup.key}
              >
                {busyId === revokeAllGroup.key && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Revoke all
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
